import {
  attendanceService,
  attendanceStructureService,
  employeeSeparationService,
  helpdeskService,
} from "../services/api";
import {
  loadRegularisationSessionHistory,
} from "./attendanceDayChecks";
import { CHANGE_WO_ACTION_LABEL } from "./weeklyOffChange";
import { mapSeparationMyRequestToDeskItem } from "../utils/lifecycleApprovalDesk";

export function formatHistoryDate(value) {
  if (!value) return "—";
  const raw = String(value).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [y, m, d] = raw.split("-");
    return `${d}-${m}-${y}`;
  }
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "—";
  const day = String(dt.getDate()).padStart(2, "0");
  const month = String(dt.getMonth() + 1).padStart(2, "0");
  return `${day}-${month}-${dt.getFullYear()}`;
}

export function formatHistoryStatus(status) {
  const key = String(status || "").toLowerCase();
  if (key === "pending") return "Pending";
  if (key === "approved") return "Approved";
  if (key === "rejected") return "Rejected";
  if (key === "cancelled" || key === "canceled") return "Cancelled";
  if (key === "submitted") return "Submitted";
  return status || "—";
}

export function historyStatusTone(status) {
  const key = String(status || "").toLowerCase();
  if (key === "approved") return "success";
  if (key === "rejected") return "danger";
  if (key === "pending" || key === "submitted") return "warning";
  return "neutral";
}

function formDataOf(row) {
  return row?.formData || row?.formJson || {};
}

export function toHistoryIsoDate(value) {
  const raw = String(value || "").trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  return "";
}

function orderedIsoRange(start, end) {
  const a = toHistoryIsoDate(start);
  const b = toHistoryIsoDate(end) || a;
  if (!a) return { start: "", end: "" };
  return a <= b ? { start: a, end: b } : { start: b, end: a };
}

/** True when the form date / to-date / range overlaps an existing On Duty span. */
export function onDutyHistoryMatchesFormRange(row, formFromDate, formToDate) {
  const selected = orderedIsoRange(formFromDate, formToDate);
  if (!selected.start) return false;
  const existing = orderedIsoRange(row?.rangeStart, row?.rangeEnd);
  if (!existing.start) return false;
  return selected.start <= existing.end && existing.start <= selected.end;
}

function matchesAction(row, actionLabel) {
  const label = String(row?.actionLabel || "").trim();
  const want = String(actionLabel || "").trim();
  if (!label || !want) return false;
  if (label === want) return true;
  if (want === "Tax Planning" && (label === "Tax Declaration" || label === "Tax Planning")) {
    return true;
  }
  if (want === CHANGE_WO_ACTION_LABEL && label === "Change WO") return true;
  return false;
}

function matchesEmployee(row, employeeId) {
  if (employeeId == null || employeeId === "") return true;
  return String(row.employeeId) === String(employeeId);
}

function deskRowToHistory(row, actionLabel) {
  const fd = formDataOf(row);
  const details = [];
  let title = actionLabel || row.actionLabel || "Request";
  let rangeStart = "";
  let rangeEnd = "";

  if (actionLabel === "Change Shift") {
    title = fd.shift || fd.newShift || "Change Shift";
    details.push({ label: "Date", value: formatHistoryDate(fd.date) });
    if (fd.currentShift) details.push({ label: "From", value: fd.currentShift });
    if (fd.shift) details.push({ label: "To", value: fd.shift });
  } else if (actionLabel === CHANGE_WO_ACTION_LABEL || actionLabel === "Change WO") {
    title = formatHistoryDate(fd.date);
    details.push({ label: "Current WO", value: formatHistoryDate(fd.date) });
    details.push({
      label: "Proposed WO",
      value: formatHistoryDate(fd.proposedDate || fd.proposedWoDate),
    });
  } else if (actionLabel === "Short Leave") {
    title = formatHistoryDate(fd.date);
    details.push({
      label: "Time",
      value: `${fd.startTime || "—"} – ${fd.endTime || "—"}`,
    });
  } else if (actionLabel === "On Duty") {
    const from = formatHistoryDate(fd.date || fd.fromDate);
    const to = formatHistoryDate(fd.toDate || fd.date || fd.fromDate);
    title = "On Duty";
    details.push({ label: "Dates", value: from === to ? from : `${from} – ${to}` });
    rangeStart = toHistoryIsoDate(fd.date || fd.fromDate);
    rangeEnd = toHistoryIsoDate(fd.toDate || fd.date || fd.fromDate) || rangeStart;
  } else if (actionLabel === "Overtime") {
    title = formatHistoryDate(fd.date);
    details.push({
      label: "Time",
      value: `${fd.startTime || "—"} – ${fd.endTime || "—"}`,
    });
  } else if (actionLabel === "Restricted Holiday") {
    title = fd.holidayName || "Restricted Holiday";
    details.push({ label: "Date", value: formatHistoryDate(fd.date) });
  } else if (actionLabel === "Regularise checks") {
    title = formatHistoryDate(fd.date || row.label);
    details.push({ label: "In", value: fd.inTime || fd.checkIn || row.checkIn || "—" });
    details.push({ label: "Out", value: fd.outTime || fd.checkOut || row.checkOut || "—" });
    details.push({ label: "Hrs", value: fd.totalHours || row.totalHours || "—" });
  } else if (actionLabel === "MRF") {
    title = fd.positionTitle || fd.requestDesignation || "MRF";
    details.push({ label: "Dept", value: fd.requestDepartment || "—" });
    details.push({ label: "Role", value: fd.requestDesignation || "—" });
  } else if (actionLabel === "Tax Planning" || actionLabel === "Tax Declaration") {
    title = fd.financialYear || "Tax Planning";
    if (fd.taxRegime) details.push({ label: "Regime", value: String(fd.taxRegime) });
  } else if (actionLabel === "Ticket") {
    title = fd.ticketSubject || fd.ticketNo || "Ticket";
    if (fd.ticketType) details.push({ label: "Type", value: fd.ticketType });
    if (fd.ticketCategory) details.push({ label: "Category", value: fd.ticketCategory });
  } else if (actionLabel === "Resignation") {
    title = formatHistoryDate(fd.separationDate || fd.resignationDate || row.separationDate);
    details.push({
      label: "Last day",
      value: formatHistoryDate(fd.lastWorkingDay || fd.lastWorkingDate || row.lastWorkingDay),
    });
  } else {
    if (fd.date) details.push({ label: "Date", value: formatHistoryDate(fd.date) });
    if (fd.fromDate) details.push({ label: "From", value: formatHistoryDate(fd.fromDate) });
    if (fd.toDate && fd.toDate !== fd.fromDate) {
      details.push({ label: "To", value: formatHistoryDate(fd.toDate) });
    }
  }

  return {
    id: String(row.id || row.requestId || `${actionLabel}-${row.submittedAt || ""}`),
    title,
    details,
    status: row.status || "Pending",
    submittedAt: row.submittedAt || row.createdAt || 0,
    rangeStart,
    rangeEnd,
  };
}

function leaveRowToHistory(row) {
  const fd = formDataOf(row);
  const from = formatHistoryDate(fd.fromDate || row.fromDate);
  const to = formatHistoryDate(fd.toDate || row.toDate);
  const code = String(row.leaveTypeCode || fd.leaveType || "").trim();
  const name = String(row.leaveTypeName || "").trim();
  const title =
    code && name && name.toLowerCase() !== code.toLowerCase()
      ? `${code} — ${name}`
      : code || name || "Leave";
  const durationKey = String(fd.leaveDuration || "").toLowerCase();
  const duration =
    durationKey === "first_half"
      ? "First Half"
      : durationKey === "second_half"
        ? "Second Half"
        : "Full Day";
  return {
    id: String(row.id || row.requestId),
    title,
    details: [
      { label: "Dates", value: from === to ? from : `${from} – ${to}` },
      { label: "Duration", value: duration },
    ],
    status: row.status || "Pending",
    submittedAt: row.submittedAt || row.createdAt || 0,
  };
}

function mergeById(rows) {
  const map = new Map();
  for (const row of rows) {
    if (!row?.id) continue;
    if (!map.has(row.id)) map.set(row.id, row);
  }
  return [...map.values()].sort((a, b) => {
    const ta = new Date(a.submittedAt || 0).getTime();
    const tb = new Date(b.submittedAt || 0).getTime();
    return tb - ta;
  });
}

async function loadDeskHistory(actionLabel, employeeId) {
  try {
    const res = await attendanceService.listDeskQuickRequests({
      employeeId: employeeId || undefined,
      actionLabel,
      limit: 200,
    });
    const rows = Array.isArray(res?.data) ? res.data : [];
    return mergeById(
      rows
        .filter((r) => matchesAction(r, actionLabel) && matchesEmployee(r, employeeId))
        .map((r) => deskRowToHistory(r, actionLabel))
    );
  } catch {
    return [];
  }
}

export async function loadEssRequestHistory({ actionLabel, employeeId }) {
  const label = String(actionLabel || "").trim();
  if (!label) return [];

  if (label === "Leave") {
    if (!employeeId) return [];
    const res = await attendanceStructureService.getLeaveRequests({
      employeeId,
      limit: 200,
    });
    const rows = Array.isArray(res?.data) ? res.data : [];
    return rows.map(leaveRowToHistory);
  }

  if (label === "Regularise checks") {
    const desk = await loadDeskHistory(label, employeeId);
    if (!employeeId) return desk;
    const session = loadRegularisationSessionHistory(employeeId).map((row) => {
      const dateIso = `${row.year}-${String(Number(row.month) + 1).padStart(2, "0")}-${String(row.day).padStart(2, "0")}`;
      const title = row.label || formatHistoryDate(dateIso);
      const match = desk.find((d) => d.title === title);
      return {
        id: String(row.id || dateIso),
        title,
        details: [
          { label: "In", value: row.checkIn ?? "—" },
          { label: "Out", value: row.checkOut ?? "—" },
          { label: "Hrs", value: row.totalHours ?? "—" },
        ],
        status: match?.status || row.status || "Pending",
        submittedAt: row.savedAt || 0,
      };
    });
    return session.length ? session : desk;
  }

  if (label === "Ticket") {
    try {
      const res = await helpdeskService.listDeskTickets();
      const mine = Array.isArray(res?.data?.mine) ? res.data.mine : [];
      return mine
        .filter((t) => matchesEmployee(t, employeeId))
        .map((t) => ({
          id: `ticket-${t.id}`,
          title: t.title || t.ticketNo || "Ticket",
          details: [
            t.typeName ? { label: "Type", value: t.typeName } : null,
            t.categoryName ? { label: "Category", value: t.categoryName } : null,
          ].filter(Boolean),
          status:
            t.status === "closed"
              ? t.rcaJson?.rejected
                ? "Rejected"
                : "Approved"
              : t.status === "resolved"
                ? "Approved"
                : "Pending",
          submittedAt: t.createdAt || 0,
        }));
    } catch {
      return loadDeskHistory(label, employeeId);
    }
  }

  if (label === "Resignation") {
    try {
      const res = await employeeSeparationService.listMyRequestsDesk();
      const rows = Array.isArray(res?.data) ? res.data : [];
      return rows
        .filter((r) => !employeeId || String(r.employeeId) === String(employeeId))
        .map((r) => {
          const mapped = mapSeparationMyRequestToDeskItem(r);
          return deskRowToHistory(
            {
              ...mapped,
              separationDate: r.separationDate,
              lastWorkingDay: r.lastWorkingDay || r.relievingDate,
            },
            "Resignation"
          );
        });
    } catch {
      return loadDeskHistory(label, employeeId);
    }
  }

  return loadDeskHistory(label, employeeId);
}
